import classes from './BrandSwitch.module.css';
import { useState, useRef, Fragment, useEffect } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import Grid from '@mui/material/Unstable_Grid2';

// IMPORT DATA
import { globals, compData } from '../../data/global-vars.js';

// IMPORT FONTAWESOME ICONS
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';

// IMPORT HOOKS, CONTEXTS AND OTHER FUNCTIONS
import { motion } from 'framer-motion';

// IMPORT OWN COMPONENTS
import Backdrop from '../UI/Backdrop';
import Animation from '../UI/Animation';
import { useBrandFind } from '../../hooks/useFinders';
import { useOpeningHours } from '../../hooks/openingHours.js';

function BrandSwitch(props) {
  const moreInformationRef = useRef();
  const params = useParams();
  const page = params.page;
  const brand = params.brand;
  const brandObject = useBrandFind(brand);
  const brandHouse = useBrandFind(brand, 'house');
  const { data: item } = useOpeningHours(brandObject);
  const { data: openingString } = useOpeningHours(brandObject, 'today');

  // disable body scroll if expanded
  const [expanded, setExpanded] = useState(false);

  function toggleExpansion() {
    setExpanded(!expanded);
  }

  function hideExpansion() {
    if (expanded) setExpanded(false);
  }

  useEffect(() => {
    props.header && updateDivHeight();
    if (expanded && props.header) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [expanded]);

  const contentRef = useRef(null);
  const [wrapperScroll, setWrapperScroll] = useState(false);
  const [wrapperScrollHeight, setWrapperScrollHeight] = useState(0);

  function updateDivHeight() {
    const wrapperHeight = window.innerHeight;
    const contentHeight = moreInformationRef.current.scrollHeight;

    if (expanded) {
      if (wrapperHeight - 60 < contentHeight && props.header) {
        setWrapperScroll(true);
        setWrapperScrollHeight(wrapperHeight - 20);
      } else setWrapperScroll(false);
    } else setWrapperScroll(false);
  }

  useEffect(() => {
    updateDivHeight(); // Initialisierung der Höhe

    const handleResize = () => {
      updateDivHeight();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [expanded]);

  let primeColor = null;
  let images = [];
  Object.values(globals.BRANDS).map((item) => {
    if (!brand) {
      primeColor = globals.BRANDS.VW.primeColor;
    }
    if (item.url === brand) {
      primeColor = item.primeColor;
      images = [
        ...images,
        <NavLink key={item.url} to={`/${item.url}/${page}/${params[0]}`}>
          <motion.div
            style={{ width: '100%' }}
            whileTap={{ scale: 0.94 }}
            variants={{
              hidden: { opacity: 0 },
              visible: { opacity: 1 },
            }}
          >
            <img
              className={`${classes.brandImage} ${item.logoStyle} select-none `}
              src={item.logo}
              alt={item.name + ' Logo'}
            />
          </motion.div>
        </NavLink>,
      ];
    } else if (item.url !== brand) {
      images = [
        ...images,
        <NavLink key={item.url} to={`/${item.url}/${page}/${params[0]}`}>
          <motion.div
            style={{ width: '100%' }}
            whileTap={{ scale: 0.94 }}
            variants={{
              hidden: { opacity: 0 },
              visible: { opacity: 1 },
            }}
          >
            <img
              className={`${classes.brandImage} ${item.logoStyle}  select-none ${classes.grayedOut}`}
              src={item.logo}
              alt={item.name + ' Logo grau'}
            />
          </motion.div>
        </NavLink>,
      ];
    }
    return null;
  });

  return (
    <Fragment>
      <Backdrop onClick={hideExpansion} show={expanded && props.header} />
      <div
        className={`${classes.wrapper} ${expanded && classes.expanded} ${
          props.header ? classes.header + ' hidden lg:block' : 'lg:hidden'
        } ${wrapperScroll && classes.scroll}`}
        style={{
          paddingLeft: 'calc(10px + env(safe-area-inset-left))',
          paddingRight: 'calc(10px + env(safe-area-inset-right))',
        }}
      >
        <div
          className={`bg-white rounded-lg select-none ${
            expanded && classes.expanded
          } ${
            props.header ? classes.header : 'sm:py-1 border border-gray-300'
          }`}
          style={{
            height: wrapperScroll && expanded && wrapperScrollHeight + 'px',
            overflowY: wrapperScroll && expanded ? 'scroll' : 'hidden',
          }}
          ref={contentRef}
        >
          <div
            className={`flex items-center text-[16px] h-[60px] cursor-pointer`}
          >
            <motion.div
              style={{
                marginTop: '2px',
                whiteSpace: 'nowrap',
                display: 'flex',
                flexDiretion: 'row',
                justifyContent: 'flex-start',
                alignItems: 'center',
              }}
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: {
                    delayChildren: 0.5,
                    staggerChildren: 0.1,
                  },
                },
              }}
              animate="visible"
              initial="hidden"
            >
              {images}
            </motion.div>
            <motion.div
              className={classes.statusInfo}
              onClick={toggleExpansion}
              whileTap={{ scale: 0.98 }}
            >
              <div className={`${classes.statusInfoText} leading-4`}>
                <Animation dir="right">
                  <span style={{ color: primeColor, fontSize: '15px' }}>
                    {openingString.textRepresentation}
                  </span>
                  <br />
                  <span style={{ fontSize: '15px' }}>
                    {brand === globals.BRANDS.SKODA.url
                      ? compData.AAM.ADRESSE.KURZFORM
                      : compData.SEYFARTH.ADRESSE.KURZFORM}
                  </span>
                </Animation>
              </div>
              <FontAwesomeIcon
                icon={faChevronRight}
                className={`${classes.expandIcon} ${
                  expanded && classes.expanded
                }`}
              />
            </motion.div>
          </div>
          <motion.div
            className={classes.statusInfoBottomText}
            onClick={toggleExpansion}
            whileTap={{ scale: 0.97 }}
            style={{ cursor: 'pointer' }}
          >
            <Animation dir="right">
              <span style={{ color: primeColor, fontSize: '15px' }}>
                {' '}
                {openingString.textRepresentation}
              </span>
              <span className={classes.address} style={{ fontSize: '15px' }}>
                {brand === globals.BRANDS.SKODA.url
                  ? compData.AAM.ADRESSE.KURZFORM
                  : compData.SEYFARTH.ADRESSE.KURZFORM}
              </span>
            </Animation>
          </motion.div>
          <div
            ref={moreInformationRef}
            className={classes.infoContainer}
            style={
              expanded
                ? {
                    maxHeight: moreInformationRef.current.scrollHeight + 'px',
                  }
                : { maxHeight: '0px' }
            }
          >
            <div className={classes.infoWrapper}>
              <div
                className={classes.emergencyShuttle}
                onClick={() => {
                  window.location.href = 'tel:+' + brandHouse.NOTFALL_LINK;
                }}
                style={{ cursor: 'pointer' }}
              >
                <span>Notdienst</span>
                <span className="pullRight">{brandHouse.NOTFALL}</span>
              </div>
              <Grid xs={12} className={classes.sectionHeader}>
                Öffnungszeiten:
              </Grid>
              <Grid
                xs={12}
                container
                className={classes.compInfoContainerText}
                spacing={1}
              >
                {item.weekdaysAsString && (
                  <Grid xs={12} container>
                    <Grid xs={'auto'} sm={4}>
                      Montag - Freitag:
                    </Grid>
                    <Grid xs="auto" sm={8}>
                      {item.weekdaysAsString}
                    </Grid>
                  </Grid>
                )}
                {item.saturdayAsString && (
                  <Grid xs={12} container>
                    <Grid xs={4} sm={4}>
                      Samstag:
                    </Grid>
                    <Grid xs="auto" sm={8}>
                      {item.saturdayAsString}
                    </Grid>
                  </Grid>
                )}
                {item.sundayAsString && (
                  <Grid xs={12} container>
                    <Grid xs={4} sm={4}>
                      Sonntag:
                    </Grid>
                    <Grid xs="auto" sm={8}>
                      {item.sundayAsString}
                    </Grid>
                  </Grid>
                )}
              </Grid>
              <Grid xs={12} className={classes.sectionHeader}>
                Kontaktdaten:
              </Grid>
              <Grid
                xs={12}
                container
                className={classes.compInfoContainerText}
                spacing={1}
              >
                <Grid
                  xs={12}
                  container
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Grid
                    xs={2}
                    sm={2}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <svg
                      width="25px"
                      height="25px"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="mx-1 my-[2px] scale-x-[-1]"
                    >
                      <path
                        d="M18.6038 12.7268L15.5652 9.08666C15.2572 8.69067 15.1032 8.49267 15.0225 8.27061C14.9511 8.07411 14.9215 7.86486 14.9357 7.65628C14.9518 7.42055 15.045 7.18766 15.2313 6.72187L15.942 4.94513C16.2177 4.25581 16.3556 3.91116 16.5934 3.68525C16.8029 3.48618 17.0656 3.3519 17.3496 3.29856C17.672 3.23803 18.0321 3.32806 18.7524 3.50812L20.7197 4.00002C20.7197 14 13.72 21 3.71973 21L3.2281 19.0324C3.04804 18.3122 2.95801 17.952 3.01854 17.6297C3.07188 17.3456 3.20616 17.083 3.40522 16.8734C3.63113 16.6356 3.97579 16.4977 4.66511 16.222L6.25064 15.5878C6.78204 15.3752 7.04773 15.2689 7.31264 15.2608C7.54678 15.2536 7.77934 15.3013 7.99171 15.4002C8.23199 15.512 8.43434 15.7144 8.83904 16.1191L11.9254 19.1569"
                        stroke="#000000"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Grid>
                  <Grid
                    xs="auto"
                    sm={8}
                    onClick={() => {
                      window.location.href =
                        'tel:+' + brandHouse.TELEFONNUMMER_LINK;
                    }}
                    sx={{ cursor: 'pointer', color: 'var(--primary-color)' }}
                  >
                    {brandHouse.TELEFONNUMMER}
                  </Grid>
                </Grid>
                <Grid
                  xs={12}
                  container
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Grid
                    xs={2}
                    sm={2}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <svg
                      height="25px"
                      width="25px"
                      viewBox="0 0 512 512"
                      className="mx-1 my-[2px]"
                    >
                      <g>
                        <path
                          d="M429.684,163.714V78.711L350.958,0h-4.916h-179.11v148.673c-12.768-10.612-29.068-17.136-46.961-17.136
		h-16.586c-20.396-0.015-38.776,8.324-51.953,21.734C38.239,166.674,30.162,185.2,30.17,205.55v221.216
		c0.008,47.015,38.219,85.218,85.235,85.234h281.192c47.015-0.016,85.219-38.219,85.234-85.234V242.262
		C481.816,206.972,460.298,176.651,429.684,163.714z M190.676,23.743h150.451l1.213,1.213V87.8h62.859l0.742,0.742v69.027
		c-3.068-0.34-6.183-0.541-9.344-0.541H190.676V23.743z M56.278,205.55c0-26.016,21.092-47.108,47.107-47.108h16.586
		c26.016,0,47.108,21.092,47.108,47.108v119.898c0,26.015-21.092,47.108-47.108,47.108h-16.586
		c-26.016,0-47.107-21.093-47.107-47.108V205.55z M454.926,426.766c0,32.214-26.116,58.33-58.33,58.33H115.404
		c-32.214,0-58.33-26.116-58.33-58.33v-47.818c12.436,10.782,28.604,17.351,46.311,17.351h16.586
		c39.07,0,70.851-31.782,70.851-70.851v-19.376v-77.165v-44.975h205.774c32.214,0,58.33,26.116,58.33,58.331V426.766z"
                        />
                        <path
                          d="M236.98,386.908c-8.494,0-15.381,6.894-15.381,15.389c0,8.494,6.887,15.38,15.381,15.38
		c8.494,0,15.388-6.886,15.388-15.38C252.367,393.802,245.473,386.908,236.98,386.908z"
                        />
                        <path
                          d="M316.131,386.908c-8.495,0-15.381,6.894-15.381,15.389c0,8.494,6.886,15.38,15.381,15.38
		c8.494,0,15.388-6.886,15.388-15.38C331.519,393.802,324.625,386.908,316.131,386.908z"
                        />
                        <path
                          d="M395.283,386.908c-8.494,0-15.381,6.894-15.381,15.389c0,8.494,6.886,15.38,15.381,15.38
		c8.502,0,15.388-6.886,15.388-15.38C410.671,393.802,403.784,386.908,395.283,386.908z"
                        />
                        <path
                          d="M236.98,336.771c-8.494,0-15.381,6.894-15.381,15.388c0,8.494,6.887,15.381,15.381,15.381
		c8.494,0,15.388-6.887,15.388-15.381C252.367,343.665,245.473,336.771,236.98,336.771z"
                        />
                        <path
                          d="M316.131,336.771c-8.495,0-15.381,6.894-15.381,15.388c0,8.494,6.886,15.381,15.381,15.381
		c8.494,0,15.388-6.887,15.388-15.381C331.519,343.665,324.625,336.771,316.131,336.771z"
                        />
                        <path
                          d="M395.283,336.771c-8.494,0-15.381,6.894-15.381,15.388c0,8.494,6.886,15.381,15.381,15.381
		c8.502,0,15.388-6.887,15.388-15.381C410.671,343.665,403.784,336.771,395.283,336.771z"
                        />
                        <path
                          d="M236.98,286.649c-8.494,0-15.381,6.894-15.381,15.388s6.887,15.381,15.381,15.381
		c8.494,0,15.388-6.887,15.388-15.381S245.473,286.649,236.98,286.649z"
                        />
                        <path
                          d="M316.131,286.649c-8.495,0-15.381,6.894-15.381,15.388s6.886,15.381,15.381,15.381
		c8.494,0,15.388-6.887,15.388-15.381S324.625,286.649,316.131,286.649z"
                        />
                        <path
                          d="M395.283,286.649c-8.494,0-15.381,6.894-15.381,15.388s6.886,15.381,15.381,15.381
		c8.502,0,15.388-6.887,15.388-15.381S403.784,286.649,395.283,286.649z"
                        />
                        <path
                          d="M396.055,231.696H237.768c-8.741,0-15.828,7.088-15.828,15.829c0,8.742,7.087,15.829,15.828,15.829h158.288
		c8.742,0,15.829-7.087,15.829-15.829C411.884,238.784,404.797,231.696,396.055,231.696z"
                        />
                      </g>
                    </svg>
                  </Grid>
                  <Grid xs="auto" sm={8}>
                    {brandHouse.FAX}
                  </Grid>
                </Grid>
                <Grid
                  xs={12}
                  container
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Grid
                    xs={2}
                    sm={2}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <svg
                      width="25px"
                      height="25px"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="mx-1 my-[2px]"
                    >
                      <path
                        d="M16 12C16 14.2091 14.2091 16 12 16C9.79086 16 8 14.2091 8 12C8 9.79086 9.79086 8 12 8C14.2091 8 16 9.79086 16 12ZM16 12V13.5C16 14.8807 17.1193 16 18.5 16V16C19.8807 16 21 14.8807 21 13.5V12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21H16"
                        stroke="#474747"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Grid>
                  <Grid xs="auto" sm={8}>
                    <a
                      href={'mailto:' + brandHouse.EMAIL}
                      style={{
                        cursor: 'pointer',
                        color: 'var(--primary-color)',
                      }}
                    >
                      {brandHouse.EMAIL}
                    </a>
                  </Grid>
                </Grid>
              </Grid>
              <Grid xs={12} className={classes.sectionHeader}>
                Anfahrt:
              </Grid>
              <Grid
                xs={12}
                container
                className={classes.compInfoContainerText}
                spacing={1}
              >
                <Grid xs={12}>
                  {brand === globals.BRANDS.SKODA.url
                    ? compData.AAM.ADRESSE.KURZFORM +
                      ', ' +
                      compData.AAM.ADRESSE.PLZ +
                      ' ' +
                      compData.AAM.ADRESSE.ORT
                    : compData.SEYFARTH.ADRESSE.KURZFORM +
                      ', ' +
                      compData.SEYFARTH.ADRESSE.PLZ +
                      ' ' +
                      compData.SEYFARTH.ADRESSE.ORT}
                </Grid>
              </Grid>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
}

export default BrandSwitch;
